/**
 * Client-side identity for ledger events.
 *
 * The client owns `eventId` so that a retry is idempotent — the server dedupes on
 * it rather than guessing whether two identical-looking deliveries are one ball sent
 * twice or two balls that scored the same (schema note in `0001_init.sql`).
 *
 * This cannot live in `@sportsos/engine`: the engine is pure, with no clock and no
 * randomness, and that purity is what makes the fixtures possible. It will move into
 * `@sportsos/sync` when the outbox lands in #13 and React Native needs the same
 * generator; until then a local module is one fewer package to reshuffle.
 */
import { asId } from "@sportsos/engine";
import type { EventId, MatchId, PlayerId, TeamId } from "@sportsos/engine";

/** The canonical 8-4-4-4-12 rendering of sixteen bytes. */
function format(bytes: Uint8Array): string {
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A UUIDv7: 48 bits of Unix milliseconds, then version and variant bits, then
 * randomness.
 *
 * Time-ordered rather than `crypto.randomUUID()`'s v4, because these become primary
 * keys in an append-only table. Random keys scatter B-tree inserts across the index;
 * time-ordered ones append to the same page, which is the difference that shows up
 * once a ledger has real volume.
 *
 * A `DataView` rather than byte indexing because `noUncheckedIndexedAccess` makes
 * every `bytes[6]` an `undefined` check, and six non-null assertions to write four
 * bytes is worse than this.
 */
export function newEventId(): EventId {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const view = new DataView(bytes.buffer);
  const ms = Date.now();

  // 48-bit big-endian timestamp, split because a JS bitwise op truncates to 32 bits
  // and the epoch in milliseconds needs 41 of them.
  view.setUint16(0, Math.floor(ms / 2 ** 32));
  view.setUint32(2, ms >>> 0);
  view.setUint8(6, (view.getUint8(6) & 0x0f) | 0x70); // version 7
  view.setUint8(8, (view.getUint8(8) & 0x3f) | 0x80); // RFC 9562 variant

  return asId<EventId>(format(bytes));
}

/**
 * A UUIDv4, built from `getRandomValues` rather than `crypto.randomUUID()`.
 *
 * **`crypto.randomUUID` only exists in a secure context** — HTTPS, or `localhost`. On a
 * phone opening the dev server over the LAN at `http://192.168.1.3:5173` it is `undefined`,
 * and the setup screen died on its first `useState` with "crypto.randomUUID is not a
 * function" before rendering anything. Production is HTTPS so this was invisible there,
 * which is exactly what made it worth removing: the failure mode is a hard crash on the
 * screen that starts every match, on the class of origin used for local device testing.
 *
 * `getRandomValues` has no such restriction and was already what `newEventId` used — so
 * this also removes an inconsistency where two id generators in one file disagreed about
 * which crypto API was safe to depend on.
 */
function uuidV4(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const view = new DataView(bytes.buffer);
  view.setUint8(6, (view.getUint8(6) & 0x0f) | 0x40); // version 4
  view.setUint8(8, (view.getUint8(8) & 0x3f) | 0x80); // RFC 9562 variant
  return format(bytes);
}

/**
 * Ids for the rows the database would generate itself.
 *
 * v4, deliberately: `players.id`, `teams.id` and `matches.id` are
 * `default gen_random_uuid()` in `0001_init.sql`, so matching that shape keeps these
 * indistinguishable from server-created rows. They are also short-lived: publishing a
 * setup replaces them with the ids the server issues (#14, `publish.ts`), because the
 * ledger references players by id and the server has never heard of a local one.
 */
export const newPlayerId = (): PlayerId => asId<PlayerId>(uuidV4());
export const newTeamId = (): TeamId => asId<TeamId>(uuidV4());
export const newMatchId = (): MatchId => asId<MatchId>(uuidV4());
